import { useHref } from "react-router";
import { type LendingAuthMode, useLendingHost } from "../../contexts/LendingHostContext";
import type { ProductType } from "../../navigation/types";
import type { LoanPortalApplication, LoanPortalStatus } from "../types";
import type { ReactLocalization } from "@fluent/react";

/**
 * Route path each product application lives at, keyed by product type. Resume
 * links here by UUID; the existing route policy resolves the correct step after
 * the application loads (Loan Portal proposal, "portal-driven resume").
 */
const PRODUCT_ROUTE_PREFIX: Record<ProductType, string> = {
  "personal-loan": "/personal-loan",
  "credit-card": "/credit-card",
  "auto-loan": "/auto-loan",
};

/**
 * Online Banking runs the parallel `olb-` flow, which is a different route tree — the web
 * flow's steps (eligibility, personal-info) assume an anonymous applicant and sit behind
 * WebFlowGuard. Unlike the web tree, a bare UUID renders nothing here, so these link to
 * confirm-info, the flow's landing page and the same target the entry point redirects to;
 * the route policy moves the user on to the canonical step from there.
 */
const OLB_PRODUCT_ROUTE_PREFIX: Record<ProductType, string> = {
  "personal-loan": "/olb-personal-loan",
  "credit-card": "/olb-credit-card",
  "auto-loan": "/olb-auto-loan",
};

/**
 * Landing path for a freshly launched, prefilled application. Both portal principals go
 * to the OLB `confirm-info` screen — that is the whole point of the sidebar launch — so
 * this deliberately ignores authMode, unlike `getApplicationPath` which resumes a
 * standalone applicant into the web tree. The `:applicationUuid` segment matches the
 * rendered confirm-info route, not the token-bootstrap loader entry.
 */
export function getOlbConfirmInfoPath(productType: ProductType, applicationUuid: string): string {
  return `${OLB_PRODUCT_ROUTE_PREFIX[productType]}/${applicationUuid}/confirm-info`;
}

export function getApplicationPath(
  application: LoanPortalApplication,
  authMode: LendingAuthMode = "lending_session"
): string {
  if (authMode === "digital_banking") {
    return `${OLB_PRODUCT_ROUTE_PREFIX[application.productType]}/${application.uuid}/confirm-info`;
  }
  return `${PRODUCT_ROUTE_PREFIX[application.productType]}/${application.uuid}`;
}

/**
 * Href for resuming an application, resolving both host concerns: which flow's routes to
 * link into, and the mount basename. These render as real anchors, which navigate the
 * browser rather than the router and so never pick up the basename on their own.
 */
export function useApplicationHref(application: LoanPortalApplication): string {
  const { authMode } = useLendingHost();
  return useHref(getApplicationPath(application, authMode));
}

export function getProductName(l10n: ReactLocalization, productType: ProductType): string {
  switch (productType) {
    case "auto-loan":
      return l10n.getString("loan-portal-product-auto-loan", null, "Auto Loan");
    case "credit-card":
      return l10n.getString("loan-portal-product-credit-card", null, "Credit Card");
    default:
      return l10n.getString("loan-portal-product-personal-loan", null, "Personal Loan");
  }
}

/** Localized display text for a portal status. */
export function getStatusLabel(l10n: ReactLocalization, status: LoanPortalStatus): string {
  return status === "submitted"
    ? l10n.getString("loan-portal-status-submitted", null, "Submitted")
    : l10n.getString("loan-portal-status-incomplete", null, "Incomplete");
}
